/**
 * OutputPane — text output display with controls for the pipeline editor.
 *
 * Presentational component: receives all data and callbacks via props.
 * Compact header with type/mode badges, visualizer dropdown, settings cog, copy, and run.
 */

import { Maximize2 } from 'lucide-react';
import { Settings } from 'lucide-react';
import React from 'react';

import { SearchBar } from '@/components/common/SearchBar';
import { TypeBadge } from '@/components/common/TypeBadge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { useRawTextSearch } from '@/hooks/useRawTextSearch';
import { formatBytes } from '@/lib/utils';
import { cn } from '@/lib/utils';
import type { VisualizerMode } from '@/store/useVisualizerStore';

import { QuickSwitchBar } from './QuickSwitchBar';

export type OutputView = 'text';

export interface OutputPaneProps {
  output: string;
  errorMessages?: string[];
  hasVisualizers: boolean;
  visualizerTabCount: number;
  outputView: OutputView;
  onOutputViewChange: (view: OutputView) => void;
  autoRunPipeline: boolean;
  onAutoRunPipelineChange: (value: boolean) => void;
  autoCopyOutputToClipboard: boolean;
  onAutoCopyOutputToClipboardChange: (value: boolean) => void;
  onCopy: () => void;
  onRun: () => void;
  isProcessing: boolean;
  isStepThroughActive: boolean;
  rerunFromStepIndex?: number | null;
  /** Pass null to hide the stats bar. */
  stats: { bytes: number; chars: number; lines: number } | null;
  /** Optional visualizer overlay — replaces the textarea when provided. */
  visualizerOverlay?: React.ReactNode;
  /** Current auto-detected semantic type for display */
  detectedTypeName?: string;
  /** Current effective visualizer mode name */
  effectiveModeName?: string;

  /** Whether a visualizer step can be added (false when one already exists) */
  canAddVisualizer?: boolean;
  /** Whether a pinned (explicit) visualizer step is active */
  isExplicitVisualizerActive?: boolean;
  /** Callback to add a visualizer step for a given mode */
  onAddVisualizerStep?: (mode: VisualizerMode) => void;
  /** Callback to remove (unpin) the visualizer step */
  onRemoveVisualizerStep?: () => void;
  /** Whether this panel is in fullscreen mode */
  isFullscreen?: boolean;
  /** Toggle fullscreen */
  onToggleFullscreen?: () => void;
  /** Quick-switch bar: activated modes for this panel */
  quickSwitchActiveModes?: VisualizerMode[];
  /** Quick-switch bar: currently active mode */
  quickSwitchCurrentMode?: string;
  /** Quick-switch bar: switch to a mode */
  onQuickSwitchMode?: (mode: VisualizerMode) => void;
  /** Quick-switch bar: remove a mode from the bar */
  onQuickSwitchRemove?: (mode: VisualizerMode) => void;
  /** Quick-switch bar: all available modes for the "+" button */
  quickSwitchAvailableModes?: VisualizerMode[];
  /** Quick-switch bar: add a mode via the "+" button */
  onQuickSwitchAdd?: (mode: VisualizerMode) => void;
}

export const OutputPane: React.FC<OutputPaneProps> = ({
  output,
  errorMessages,
  onRun,
  isProcessing,
  isStepThroughActive,
  rerunFromStepIndex,
  stats,
  visualizerOverlay,
  autoRunPipeline,
  onAutoRunPipelineChange,
  autoCopyOutputToClipboard,
  onAutoCopyOutputToClipboardChange,
  onCopy,
  detectedTypeName,
  effectiveModeName: _effectiveModeName,
  isFullscreen,
  onToggleFullscreen,
  quickSwitchActiveModes,
  quickSwitchCurrentMode,
  onQuickSwitchMode,
  onQuickSwitchRemove,
  quickSwitchAvailableModes,
  onQuickSwitchAdd,
}) => {
  // FR-SEARCH-1: In-panel search for raw text view
  const search = useRawTextSearch(output);

  return (
  <div ref={search.containerRef} className="flex-1 min-w-0 flex flex-col">
    {/* Compact panel header */}
    <div className="h-6 flex items-center gap-1.5 px-2.5 border-b border-border bg-background shrink-0">
      <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground shrink-0">
        OUTPUT
      </span>

      {/* Type badge */}
      {detectedTypeName && (
        <TypeBadge type={detectedTypeName} isAuto={true} />
      )}

      {/* Quick-switch tab bar (replaces old mode selector dropdown) */}
      {quickSwitchActiveModes && quickSwitchCurrentMode && onQuickSwitchMode && onQuickSwitchRemove && (
        <QuickSwitchBar
          activeModes={quickSwitchActiveModes}
          currentMode={quickSwitchCurrentMode}
          onSwitchMode={onQuickSwitchMode}
          onRemoveMode={onQuickSwitchRemove}
          availableModes={quickSwitchAvailableModes}
          onAddMode={onQuickSwitchAdd}
        />
      )}

      {isProcessing && rerunFromStepIndex != null && (
        <span className="text-[9px] uppercase tracking-wider text-amber-400/90 shrink-0">
          Re-running from step {rerunFromStepIndex + 1}
        </span>
      )}

      {/* Action buttons */}
      <div className="flex items-center gap-1.5 ml-auto shrink-0">
        {visualizerOverlay && onToggleFullscreen && (
          <button
            onClick={onToggleFullscreen}
            className="h-[18px] w-[18px] flex items-center justify-center text-muted-foreground hover:text-foreground rounded border border-border bg-transparent hover:bg-white/[0.03] transition-all duration-150"
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            <Maximize2 className="h-2.5 w-2.5" />
          </button>
        )}

        {/* Settings popover: auto-run + auto-copy */}
        <Popover>
          <PopoverTrigger asChild>
            <button
              className="h-[18px] w-[18px] flex items-center justify-center text-muted-foreground hover:text-foreground rounded border border-border bg-transparent hover:bg-white/[0.03] transition-all duration-150"
              title="Output settings"
            >
              <Settings className="h-2.5 w-2.5" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-48 p-0">
            <div className="px-3 py-2 flex items-center justify-between gap-3">
              <span className="text-xs text-muted-foreground">Auto Run</span>
              <Switch checked={autoRunPipeline} onCheckedChange={onAutoRunPipelineChange} />
            </div>
            <div className="px-3 py-2 flex items-center justify-between gap-3 border-t border-border">
              <span className="text-xs text-muted-foreground">Auto Copy</span>
              <Switch checked={autoCopyOutputToClipboard} onCheckedChange={onAutoCopyOutputToClipboardChange} />
            </div>
          </PopoverContent>
        </Popover>

        {/* Copy button */}
        <button
          onClick={onCopy}
          className="h-[18px] px-1.5 text-[10px] text-muted-foreground hover:text-foreground rounded border border-border bg-transparent hover:bg-white/[0.03] transition-all duration-150"
          title="Copy output"
        >
          Copy
        </button>

        {/* Run button — primary violet */}
        <button
          onClick={onRun}
          disabled={isProcessing || isStepThroughActive}
          className={cn(
            'h-[18px] px-2 text-[10px] font-semibold rounded border transition-all duration-200',
            'inline-flex items-center gap-1',
            isProcessing
              ? 'bg-amber-400 border-amber-400 text-zinc-900 animate-pulse'
              : 'bg-violet-400 border-violet-400 text-zinc-900 hover:bg-violet-300 hover:border-violet-300 hover:shadow-[0_0_20px_rgba(167,139,250,0.1)]',
            (isProcessing || isStepThroughActive) && 'opacity-75 cursor-default'
          )}
        >
          ▶ {isProcessing ? 'Running…' : 'Run'}
        </button>
      </div>
    </div>

    {/* Error banner */}
    {errorMessages && errorMessages.length > 0 && (
      <div className="px-3 py-2 bg-destructive/10 border-b border-destructive/20 text-destructive text-xs font-mono space-y-0.5">
        {errorMessages.map((msg, i) => (
          <div key={i}>⚠ {msg}</div>
        ))}
      </div>
    )}

    {/* Content */}
    {visualizerOverlay ?? (
      <div className="flex-1 flex flex-col min-h-0">
        {search.isOpen && (
          <div className="shrink-0 border-b border-border">
            <SearchBar
              ref={search.searchInputRef}
              query={search.query}
              onQueryChange={search.setQuery}
              placeholder="Find in output…"
              resultCount={search.matchCount}
              currentIndex={search.currentIndex}
              onNext={search.goToNext}
              onPrev={search.goToPrev}
            />
          </div>
        )}
        <textarea
          className="flex-1 p-4 bg-background resize-none font-mono text-sm focus:outline-none"
          placeholder="Output will appear here..."
          value={output}
          readOnly
          spellCheck={false}
        />
      </div>
    )}

    {/* Stats footer */}
    {stats && (
      <div className="h-4 flex items-center px-2.5 text-[9px] text-muted-foreground border-t border-border bg-muted/30 gap-1.5 shrink-0">
        <span>{formatBytes(stats.bytes)}</span>
        <span>·</span>
        <span>{stats.chars.toLocaleString()} ch</span>
        <span>·</span>
        <span>{stats.lines.toLocaleString()} ln</span>
      </div>
    )}
  </div>
);
};


